import { Button, Input, Skeleton, Tooltip } from "antd";
import { SearchOutlined, ArrowLeftOutlined, SolutionOutlined } from "@ant-design/icons";
import ReusableTable from "../../Reuseable/ReusableTable/ReusableTable";
import PanelCard from "../DashboardPage/components/PanelCard";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchFieldAgentDetails } from "../../store/telecaller/cases";
import { useNavigate, useParams } from "react-router-dom";
import { formatCurrency } from "../../assets/helpers";
import "./FieldAgentAllocation.scss";

const EMPTY = "—";

/* The API field for these two was never settled in this table - Phone No read
   `role` and Amount Overdue read `full_name`, so both columns rendered "NA" on
   every row. These read the names used by the other case tables and fall back
   across the plausible spellings rather than binding to one guess. */
const pickPhone = (record) =>
  record?.phone_number ?? record?.mobile_no ?? record?.phone ?? record?.contact_no;

const pickOverdue = (record) =>
  record?.amount_overdue ?? record?.odpos ?? record?.total_pos;

const ViewAgentDetails = () => {
    const { field_agent_details, field_agent_details_loading } = useSelector(state => state.telecallerCases)
    const { format } = useSelector((state) => state.priceFormat);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const loading = field_agent_details_loading;
    const [params, setParams] = useState({
        search: ''
    })

    useEffect(() => {
        dispatch(fetchFieldAgentDetails({ id, query: params }))
    }, [dispatch, id, params])

    const rows = useMemo(
      () => (Array.isArray(field_agent_details?.data) ? field_agent_details.data : []),
      [field_agent_details]
    );

    /* Skeleton rows need their own keys - Array(5).fill({}) gave every
       placeholder row the same (undefined) id. */
    const skeletonData = useMemo(
      () => Array(5).fill({}).map((_, i) => ({ id: `skeleton-${i}` })),
      []
    );

    const handleViewClick = () => {

    }
    const column = [
        {
            title: 'Customer Name',
            dataIndex: 'customer_name',
            key: 'customer_name',
            render: (text) =>
              loading ? (
                <Skeleton.Input size="small" active />
              ) : text ? (
                <span className="faCustomer">{text}</span>
              ) : (
                <span className="faEmpty">{EMPTY}</span>
              ),
        },
        {
            title: 'Loan No',
            dataIndex: 'loan_no',
            key: 'loan_no',
            render: (text) =>
              loading ? (
                <Skeleton.Input size="small" active />
              ) : text ? (
                <span className="faLoanNo">{text}</span>
              ) : (
                <span className="faEmpty">{EMPTY}</span>
              ),
        },
        {
            title: 'Phone No',
            key: 'phone_no',
            render: (_, record) => {
              if (loading) return <Skeleton.Input size="small" active />;
              const phone = pickPhone(record);
              if (!phone) return <span className="faEmpty">{EMPTY}</span>;
              return <span className="faPhone">{phone}</span>;
            },
        },
        {
            title: 'Bucket',
            dataIndex: 'bucket',
            key: 'bucket',
            render: (text) =>
              loading ? (
                <Skeleton.Input size="small" active />
              ) : text ? (
                <span className="faBucket">{text}</span>
              ) : (
                <span className="faEmpty">{EMPTY}</span>
              ),
        },
        {
            title: 'Amount Overdue',
            key: 'amount_overdue',
            align: 'right',
            render: (_, record) => {
              if (loading) return <Skeleton.Input size="small" active />;
              const value = pickOverdue(record);
              if (value === null || value === undefined || value === "") {
                return <span className="faEmpty">{EMPTY}</span>;
              }
              return <span className="faAmount">{formatCurrency(value, format)}</span>;
            },
        },
        {
            title: 'Bank',
            dataIndex: 'bank',
            key: 'bank',
            render: (text) =>
              loading ? (
                <Skeleton.Input size="small" active />
              ) : text ? (
                <span className="faText">{text}</span>
              ) : (
                <span className="faEmpty">{EMPTY}</span>
              ),
        },
        {
            title: 'Actions',
            key: 'actions',
            align: 'center',
            width: 160,
            render: (_, record) => {
              if (loading) return <Skeleton.Input size="small" active />;
              return (
                <Tooltip title="Record feedback for this case">
                  <Button
                    className="faFeedbackBtn"
                    onClick={() => handleViewClick(record)}
                  >
                    Give Feedback
                  </Button>
                </Tooltip>
              );
            },
        },
    ]

    return (
        <div className="fieldAgentAllocationPage">
            {/* Landing here from the agent list, there was nothing on screen
                saying which agent's cases these are, and no way back. */}
            <div className="faDetailsHeader">
              <button
                type="button"
                className="faBackBtn"
                onClick={() => navigate("/field-agent-allocation")}
                aria-label="Back to field agents"
              >
                <ArrowLeftOutlined />
              </button>
              <div className="faDetailsHeader__titles">
                <h2>Allocated Cases</h2>
                <p>Cases currently assigned to this field agent.</p>
              </div>
            </div>

            <PanelCard
              title="Cases"
              subtitle={
                loading
                  ? "Loading cases…"
                  : `${rows.length} case${rows.length === 1 ? "" : "s"} allocated`
              }
              icon={<SolutionOutlined />}
              accent="brand"
              padded={false}
              actions={
                <Input
                    placeholder="Search cases"
                    allowClear
                    className="faSearchInput"
                    prefix={<SearchOutlined />}
                    onPressEnter={(e) => {
                      const value = e.target.value;
                      setParams((prev) => ({
                        ...prev,
                          search: value,
                      }));
                    }}
                    onClear={() => {
                      setParams((prev) => ({
                        ...prev,
                        search: '',
                      }));
                    }}
                />
              }
            >
              <div className="field-agent-report-table">
                <ReusableTable
                    columns={column}
                    dataSource={loading ? skeletonData : rows}
                    rowKey="id"
                    /* the footer read "Showing 0 - 0 of 0 items" over a full
                       table because no total was ever passed */
                    totalItems={rows.length}
                />
              </div>
            </PanelCard>
        </div>
    );
};

export default ViewAgentDetails;
